import { create } from 'zustand'
import { queueCollectedSync } from '@/lib/flashcardSync'

// "Collected" flashcards — a concept becomes collected the first time it reaches
// Level 1, i.e. its first correct quiz answer (collectLevelledConcepts in
// stores/quizStore.ts; docs/flashcard-collection.md). Collecting is distinct from manually adding a
// concept to the flashcard gallery (useFlashcards). State is persisted to
// localStorage, mirroring the offline-first pattern used elsewhere in the app,
// and — for signed-in users — mirrored to the user_collected_cards table so the
// set follows the learner across devices (see lib/flashcardSync.ts). The write
// is fire-and-forget: localStorage stays the immediate source of truth, so this
// store's API is still fully synchronous.

export interface CollectedCard {
  name: string
  collectedAt: number
}

const STORAGE_KEY = 'actuarial_collected_cards'

// Fired on every successful collection so the always-mounted nav — the
// sidebar's Flashcards row, and below lg the hamburger the deck now lives
// behind — can light up without subscribing to the store.
export const COLLECTED_EVENT = 'flashcard-collected'

function load(): CollectedCard[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as CollectedCard[]
    if (!Array.isArray(parsed)) return []
    return parsed.filter(c => c && typeof c.name === 'string')
  } catch {
    return []
  }
}

function persist(cards: CollectedCard[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cards))
  } catch { /* ignore quota errors */ }
}

interface CollectedCardsState {
  cards: CollectedCard[]
  isCollected: (name: string) => boolean
  /**
   * Marks a concept collected. `silent` skips COLLECTED_EVENT — used when
   * back-filling a card the user demonstrably already owns (mastery past New)
   * rather than one they just won, so the nav doesn't celebrate a collection
   * that isn't happening now.
   */
  collect: (name: string, opts?: { silent?: boolean }) => void
  uncollect: (name: string) => void
  /**
   * Replace the whole set from the server (see hooks/useFlashcardSync). Writes
   * through to localStorage but does *not* queue a server push — the caller
   * decides whether the merged state needs pushing back.
   */
  hydrate: (cards: CollectedCard[]) => void
}

export const useCollectedCards = create<CollectedCardsState>((set, get) => ({
  cards: load(),
  isCollected: name => get().cards.some(c => c.name.toLowerCase() === name.toLowerCase()),
  collect: (name, opts) => {
    const { cards } = get()
    if (cards.some(c => c.name.toLowerCase() === name.toLowerCase())) return
    const next = [...cards, { name, collectedAt: Date.now() }]
    persist(next)
    queueCollectedSync(next)
    set({ cards: next })
    if (opts?.silent) return
    try {
      window.dispatchEvent(new CustomEvent(COLLECTED_EVENT, { detail: { name } }))
    } catch { /* ignore */ }
  },
  uncollect: name => {
    const next = get().cards.filter(c => c.name.toLowerCase() !== name.toLowerCase())
    persist(next)
    queueCollectedSync(next)
    set({ cards: next })
  },
  hydrate: cards => {
    persist(cards)
    set({ cards })
  },
}))
